import { IPrecedingStage, StageDetails } from "../../../types/tournament";
import { RankedGroupTeam } from "../types";
import { Card, Checkbox, DescriptionList, Field, FormGrid, Loader, Select, StatCard, Tabs, Tag } from "../../../ui";
import StepHeader from "./StepHeader";

interface Props {
  stage?: StageDetails;
  stageDependent: boolean;
  activeStageTab?: IPrecedingStage;
  setActiveStageTab: (v: IPrecedingStage) => void;
  selectedStages: IPrecedingStage[];
  setSelectedStages: React.Dispatch<React.SetStateAction<IPrecedingStage[]>>;
  createWithEmptyTeams: boolean;
  setCreateWithEmptyTeams: (v: boolean) => void;
  rankedGroupTeams: RankedGroupTeam[];
  rankedTeamsLoading: boolean;
  fetchAndRankGroupTeams: (stageId: string) => Promise<RankedGroupTeam[]>;
}

const QualificationRulesStep = ({
  stage, stageDependent, activeStageTab, setActiveStageTab, selectedStages, setSelectedStages,
  createWithEmptyTeams, setCreateWithEmptyTeams, rankedGroupTeams, rankedTeamsLoading, fetchAndRankGroupTeams,
}: Props) => {
  const stageTabs = stage?.precedingStages.filter((x) => selectedStages.findIndex((s) => s.id === x.id) !== -1) || [];
  const dropdownText =
    activeStageTab?.stageName === "League"
      ? "How many teams qualify from the top?"
      : activeStageTab?.stageName === "Group"
      ? createWithEmptyTeams ? "Select qualifying position range (teams will be filled later)" : "Select qualifying position range"
      : "How many of the finalists qualify?";

  return (
    <div className="cs-step qualification-rules-container">
      <StepHeader title="Qualification Rules" subtitle="What are the rules for of qualification for the selected stages?" />
      {stage?.formatName === "Knockout" && stageDependent && (
        <Card padding="sm" className="empty-teams-option">
          <Checkbox
            checked={createWithEmptyTeams}
            onChange={(checked) => setCreateWithEmptyTeams(checked)}
            label="Create knockout stage with empty teams (teams will be filled once precedent stages are completed)"
          />
        </Card>
      )}
      <StatCard
        className="qualifying-teams-count"
        label="Total # of qualifying teams"
        value={`${selectedStages.reduce((a, b) => a + (b.stageName === "Group" ? b.value : b.value), 0)} teams`}
      />
      <Tabs
        className="stages-tabs-list"
        ariaLabel="Stages"
        options={stageTabs.map((item) => ({ value: item.id, label: item.stageName }))}
        value={activeStageTab?.id ?? ""}
        onChange={(stageId) => {
          const item = stageTabs.find((x) => x.id === stageId);
          if (item) setActiveStageTab(item);
        }}
      />
      <div className="cs-qual-grid">
        <Card title="Stage Details" className="stage-details-container">
          <DescriptionList
            columns={2}
            items={[
              { label: "Format", value: activeStageTab?.stageName },
              ...(activeStageTab?.stageName === "Knockout"
                ? [
                    { label: "Mode", value: activeStageTab.knockoutRules.mode === "single" ? "Single Elimination" : "Double Elimination" },
                    { label: "Third Place Match", value: activeStageTab.knockoutRules.hasThirdPlaceMatch ? "Yes" : "No" },
                  ]
                : [
                    { label: "Encounters", value: activeStageTab?.leagueRules.encounters },
                    { label: "Ranking Criteria", value: activeStageTab?.leagueRules.criteria === "total" ? "Total Points" : "Direct Matches" },
                    { label: "Points for Win", value: activeStageTab?.leagueRules.pointsForWin },
                    { label: "Points for Loss", value: activeStageTab?.leagueRules.pointsForLoss },
                  ]),
            ]}
          />
        </Card>
        <Card title={dropdownText} className="dropdown-container">
          {activeStageTab?.stageName === "Group" ? (
            <div className="position-range-selectors">
              <FormGrid columns={2}>
                <Field label="From Position">
                  {(fieldId) => (
                    <Select
                      id={fieldId}
                      block
                      value={selectedStages.find((x) => x.id === activeStageTab?.id)?.fromPosition || 1}
                      onChange={(e) => {
                        const fromPos = Number(e.target.value);
                        setSelectedStages((prev) => {
                          const updatedStages = [...prev];
                          const idx = updatedStages.findIndex((x) => x.id === activeStageTab?.id);
                          const currentToPos = updatedStages[idx].toPosition || fromPos;
                          const toPos = Math.max(fromPos, currentToPos);
                          updatedStages[idx] = { ...updatedStages[idx], fromPosition: fromPos, toPosition: toPos, value: toPos - fromPos + 1 };
                          return updatedStages;
                        });
                        if (!createWithEmptyTeams) fetchAndRankGroupTeams(activeStageTab.id);
                      }}
                    >
                      {Array(createWithEmptyTeams ? activeStageTab?.totalTeams || 0 : rankedGroupTeams.length || 0).fill(0).map((_, idx) => (
                        <option value={idx + 1} key={idx}>{idx + 1}</option>
                      ))}
                    </Select>
                  )}
                </Field>
                <Field label="To Position">
                  {(fieldId) => (
                    <Select
                      id={fieldId}
                      block
                      value={selectedStages.find((x) => x.id === activeStageTab?.id)?.toPosition || 1}
                      onChange={(e) => {
                        const toPos = Number(e.target.value);
                        setSelectedStages((prev) => {
                          const updatedStages = [...prev];
                          const idx = updatedStages.findIndex((x) => x.id === activeStageTab?.id);
                          const currentFromPos = updatedStages[idx].fromPosition || 1;
                          const fromPos = Math.min(currentFromPos, toPos);
                          updatedStages[idx] = { ...updatedStages[idx], fromPosition: fromPos, toPosition: toPos, value: toPos - fromPos + 1 };
                          return updatedStages;
                        });
                        if (!createWithEmptyTeams) fetchAndRankGroupTeams(activeStageTab.id);
                      }}
                    >
                      {Array(createWithEmptyTeams ? activeStageTab?.totalTeams || 0 : rankedGroupTeams.length || 0).fill(0).map((_, idx) => (
                        <option value={idx + 1} key={idx}>{idx + 1}</option>
                      ))}
                    </Select>
                  )}
                </Field>
              </FormGrid>
              <p className="position-info">
                Teams from position {selectedStages.find((x) => x.id === activeStageTab?.id)?.fromPosition || 1} to{" "}
                {selectedStages.find((x) => x.id === activeStageTab?.id)?.toPosition || 1} in overall standings (
                {selectedStages.find((x) => x.id === activeStageTab?.id)?.value || 0} teams total)
              </p>
            </div>
          ) : (
            <Select
              aria-label={dropdownText}
              className="cs-select-narrow"
              value={selectedStages.find((x) => x.id === activeStageTab?.id)?.value ?? ""}
              onChange={(e) => {
                setSelectedStages((prev) => {
                  const updatedStages = [...prev];
                  const idx = updatedStages.findIndex((x) => x.id === activeStageTab?.id);
                  updatedStages[idx] = { ...updatedStages[idx], value: Number(e.target.value) };
                  return updatedStages;
                });
              }}
            >
              {Array(activeStageTab?.stageName === "League" ? activeStageTab?.totalTeams : 1).fill(0).map((_, idx) => (
                <option value={idx + 1} key={idx}>{idx + 1}</option>
              ))}
            </Select>
          )}
        </Card>
      </div>
      {activeStageTab?.stageName === "Group" && !createWithEmptyTeams && rankedTeamsLoading && (
        <Card className="ranked-teams-loading">
          <Loader label="Loading team rankings..." minHeight={140} />
        </Card>
      )}
      {activeStageTab?.stageName === "Group" && rankedGroupTeams.length > 0 && !createWithEmptyTeams && !rankedTeamsLoading && (
        <Card
          className="ranked-teams-display"
          padding="none"
          title="Ranked Teams (Overall Standings)"
          subtitle={
            <>
              Highlighting teams from positions {selectedStages.find((x) => x.id === activeStageTab?.id)?.fromPosition || 1} to{" "}
              {selectedStages.find((x) => x.id === activeStageTab?.id)?.toPosition || 1} (
              {selectedStages.find((x) => x.id === activeStageTab?.id)?.value || 0} teams total)
            </>
          }
        >
          <div className="ranked-teams-list">
            {rankedGroupTeams.map((team, index) => {
              const fromPos = selectedStages.find((x) => x.id === activeStageTab?.id)?.fromPosition || 1;
              const toPos = selectedStages.find((x) => x.id === activeStageTab?.id)?.toPosition || 1;
              const overallPosition = index + 1;
              const isQualifying = overallPosition >= fromPos && overallPosition <= toPos;
              return (
                <div key={team.teamId} className={`ranked-team-item ${isQualifying ? "qualifying" : ""}`}>
                  <div className="team-rank">
                    <span className="rank-number">#{index + 1}</span>
                    {isQualifying && <Tag tone="spark" className="qualifying-badge">✓</Tag>}
                  </div>
                  <div className="team-info">
                    <p className="team-name">{team.teamName}</p>
                    <p className="team-details">
                      Group {team.groupName} • {team.position}
                      {team.position === 1 ? "st" : team.position === 2 ? "nd" : team.position === 3 ? "rd" : "th"} place
                    </p>
                  </div>
                  <div className="team-stats">
                    <span className="stat"><strong>{team.points}</strong> pts</span>
                    <span className="stat"><strong>{team.setsDifference > 0 ? "+" : ""}{team.setsDifference}</strong> sets</span>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
};

export default QualificationRulesStep;
